import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Container, Tab, Tabs, Box, Typography, Button, TextField, MenuItem, IconButton, Tooltip } from "@material-ui/core";
import { Title } from "react-admin";
import { League, LeagueFilter, LeagueStat } from "./LeagueModels";
import LeagueUtil from "./LeagueUtil";
import LeagueTable from "./LeagueTable";
import CreateRoundUtil from "../CreateRound/CreateRoundUtil";
import RoundOverviewUtil from "../RoundOverview/RoundOverviewUtil";
import RefreshIcon from '@material-ui/icons/Refresh';
import LeagueOverviewFixtures from "./LeagueOverviewFixtures";
import LeagueMappingView from "./LeagueMappingView";
import ExtLeagueView from "./ExtLeagueView";
import FixtureMappingView from "../FixtureOverview/FixtureMappingView";
import ExtFixtureView from "../FixtureOverview/ExtFixtureView";
import { OverviewCriteria } from "../Ticker2/Ticker2Models";

interface TabPanelProps {
    children: React.ReactNode;
    value: number;
    index: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tab-panel-${index}`}
            aria-labelledby={`tab-${index}`}
        >
            {value === index && (
                <Box p={3}>
                    <Typography component="div">{children}</Typography>
                </Box>
            )}
        </div>
    );
}

// export interface LeagueOverviewState {
//     leagueName: string;
//     enabled?: boolean;
//     pageSize: number;
//     page: number;
//     orderBy: string;
//     orderDirection: string;
// }
export interface LeagueOverviewState {
    leagueName: string;
    enabled: string;
    pageSize: string;
}

interface LeagueItemState {
    items: League[];
    lastId: number;
    more: boolean;
}

const LeagueOverview: React.FC = () => {
    const [itemState, setItemState] = useState<LeagueItemState>({ items: [], lastId: 0, more: false });
    const [selectedItem, setSelectedItem] = useState<League | undefined>(undefined);
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const [formData, setFormData] = useState<LeagueOverviewState>({
        leagueName: "",
        enabled: "*",
        pageSize: "100"
    });
    const [actionError, setActionError] = useState<string>("");
    const [leagueStat, setLeagueStat] = useState<LeagueStat | null>(null);
    const history = useHistory();

    const toFilter = (state: LeagueOverviewState) : LeagueFilter => {
        var enabled : boolean | undefined = undefined;
        if (state.enabled === "true") {
            enabled = true;
        } else if (state.enabled === "false") {
            enabled = false;
        }
        return {
            q: state.leagueName.length === 0 ? undefined : state.leagueName,
            enabled: enabled
        };
    };

    const getPageSize = (state: LeagueOverviewState) : number => {
        var pageSize = parseInt(state.pageSize);
        return isNaN(pageSize) || pageSize < 1 ? 100 : pageSize;
    };

    const refreshLeagues = (caller: string, more: boolean) => {
        var pageSize = getPageSize(formData);
        var lastId = more ? itemState.lastId : 0;
        setActionError("");
        LeagueUtil.getLeagues(pageSize, lastId, toFilter(formData)).then((resp) => {
            if (!resp.success) {
                setActionError(`${caller} error: ${resp.message}`);
                return;
            }
            var newItems = resp.item?.items ?? [];
            var items = lastId > 0 ? itemState.items.concat(newItems) : newItems;
            setItemState({
                items: items,
                lastId: items.length > 0 ? items[items.length - 1].id : 0,
                more: newItems.length >= pageSize
            });
        }).catch(error => {
            setActionError(CreateRoundUtil.getExceptionMessage(error, false, `${caller} error`));
        });
    };

    const refreshStat = (caller: string) => {
        LeagueUtil.getLeagueStat().then(resp => {
            if (resp.success && resp.item) {
                setLeagueStat(resp.item);
                return;
            }
            var message = resp.success ? `${caller} stat failure: no data` : `${caller} stat error: ${resp.message}`;
            setActionError(prev => prev.length === 0 ? message : prev + " | " + message);
        }).catch(error => {
            var message = CreateRoundUtil.getExceptionMessage(error, false, `${caller} stat error`);
            setActionError(prev => prev.length === 0 ? message : prev + " | " + message);
        });
    };

    useEffect(() => {
        refreshLeagues("Init leagues", false);
        refreshStat("Init");
    }, []);


    const onSelect = (item?: League) => {
        setSelectedItem(item);
    };

    const handleChange = (name: string, value: any) : void => {
        var fd = { ...formData, [name]: value };
        setFormData(fd);
    };
    const onSearch = () : void => {
        refreshLeagues("Search", false);
    };
    const onMore = () : void => {
        if (itemState.lastId <= 0) {
            setActionError("More failure: LastId was not set.");
            return;
        }
        refreshLeagues("More", true);
    };
    const onRefreshStat = () : void => {
        setActionError("");
        refreshStat("Refresh");
     };
 
    var leagueNameError = "";
    var pageSizeError = "";
    var tmpInt = 0;
    var valid = true;
    if (formData.pageSize.length === 0) {
        valid = false;
        pageSizeError = "Required int";
    } else {
        tmpInt = parseInt(formData.pageSize);
        if (isNaN(tmpInt)) {
            valid = false;
            pageSizeError = "Invalid int";
        } else if (tmpInt < 1) {
            pageSizeError = "Less than 1";
        }
    }

    var actionErrorBox = null;
    if (actionError.length > 0) {
        actionErrorBox = (<Box fontSize={10} color={RoundOverviewUtil.INVALID_COLOR}>{actionError}</Box>);
    }

    var enabledCount = 0;
    itemState.items.forEach(it => {
        if (it.enabled) {
            enabledCount++;
        }
    });
    // The ng leagues endpoint is keyset paged and does not return a total count.
    var responseInfo = `Count: ${itemState.items.length} | EnabledCount: ${enabledCount}`;
    var responseInfoBox =  (<Box fontSize={10}>{responseInfo}</Box>);;
    var statBox = null;
    var statInfo = null;
    if (leagueStat) {
        statInfo = `Statistics - Count: ${leagueStat.count} | EnabledCount: ${leagueStat.enabledCount}`;
    } else {
        statInfo = "Statistics - No data";
    }
    var leagueInfo = "League - Not selected";
    if (selectedItem) {
        leagueInfo = `League - Id:${selectedItem.id} | Name: ${selectedItem.name}`;
    }

    return (
        <Container maxWidth="xl">
            <Title title="League Overview" />
            <Box paddingBottom={2}>
                <TextField
                        name="leagueName"
                        label="League pattern"
                        value={formData.leagueName}
                        error={leagueNameError && leagueNameError.length > 0 ? true : false}
                        helperText={leagueNameError && leagueNameError.length > 0 ? leagueNameError : "Character sequence of League name"}
                        onChange={e => handleChange("leagueName", e.target.value)}
                />
                    <TextField
                        name="enabled"
                        label="Enabled"
                        select
                        value={formData.enabled}
                        onChange={e => handleChange("enabled", e.target.value)}
                        style={{ width: 100 }}
                    >
                        <MenuItem key="*" value="*">(All)</MenuItem>
                        <MenuItem key="false" value="false">false</MenuItem>
                        <MenuItem key="true" value="true">true</MenuItem>
                    </TextField>
                    <TextField
                        name="pageSize"
                        label="Page size"
                        type="number"
                        value={formData.pageSize}
                        style={{ width: 100 }}
                        error={pageSizeError && pageSizeError.length ? true : false}
                        helperText={pageSizeError}
                        onChange={e => handleChange("pageSize", e.target.value)}
                    />
                    <Button 
                        variant="contained"
                        color="secondary"
                        size="small"
                        disabled={!valid}
                        onClick={e => onSearch()}>
                        Search
                    </Button>&nbsp;
                    <Button 
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={!valid || !itemState.more}
                        onClick={e => onMore()}>
                        More
                    </Button>
            </Box>
            {/* <Box paddingBottom={1}>
                <TextField
                        name="orderBy"
                        label="Order by"
                        select
                        value={formData.orderBy}
                        onChange={e => handleChange("orderBy", e.target.value)}
                        style={{ width: 100 }}
                    >
                        <MenuItem key="id" value="id">Id</MenuItem>
                        <MenuItem key="name" value="name">Name</MenuItem>
                        <MenuItem key="enabled" value="enabled">Enabled</MenuItem>
                </TextField>
                <TextField
                        name="orderDirection"
                        label="Order dir"
                        select
                        value={formData.orderDirection}
                        onChange={e => handleChange("orderDirection", e.target.value)}
                        style={{ width: 100 }}
                    >
                        <MenuItem key="asc" value="asc">asc</MenuItem>
                        <MenuItem key="desc" value="desc">desc</MenuItem>
                </TextField>
                <TextField
                    name="pageSize"
                    label="Page size"
                    type="number"
                    value={formData.pageSize}
                    style={{ width: 100 }}
                    error={pageSizeError && pageSizeError.length ? true : false}
                    helperText={pageSizeError}
                    onChange={e => handleChange("pageSize", e.target.value)}
                />
                <TextField
                    name="page"
                    label="Page"
                    type="number"
                    value={formData.page}
                    style={{ width: 100 }}
                    error={pageError && pageError.length ? true : false}
                    helperText={pageError}
                    onChange={e => handleChange("page", e.target.value)}
                />
                <Button 
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={!valid}
                    onClick={e => onSearch()}>
                    Search
                </Button>&nbsp;
                </Box> */}
                {actionErrorBox}
                {responseInfoBox}
                <Box fontSize={10}>
                    {statInfo}
                    <IconButton aria-label="refreshStat" color="secondary" size="small" onClick={(e: any) => onRefreshStat()} >
                        <Tooltip title="Refresh statistics">
                            <RefreshIcon />
                        </Tooltip>
                    </IconButton>

                </Box>
            <LeagueTable leagues={itemState.items} onSelect={onSelect} />
            <Box fontSize={10}>
                {leagueInfo}
            </Box>
            <div>
                <Tabs value={selectedTab}
                    onChange={(ev, value) => { setSelectedTab(value) }}
                >
                    <Tab label="Fixtures" />
                    <Tab label="League mappings" />
                    <Tab label="Ext leagues" />
                    <Tab label="Fixture mappings" />
                    <Tab label="Ext fixtures" />
                </Tabs>
                <TabPanel value={selectedTab} index={0}>
                    <LeagueOverviewFixtures leagueId={selectedItem ? selectedItem.id : 0} leagueName={selectedItem ? selectedItem.title : ""} />
                </TabPanel>
                <TabPanel value={selectedTab} index={1}>
                    <LeagueMappingView leagueId={selectedItem ? selectedItem.id : 0} leagueName={selectedItem ? selectedItem.title : ""} />
                </TabPanel>
                <TabPanel value={selectedTab} index={2}>
                    <ExtLeagueView leagueId={selectedItem ? selectedItem.id : 0} leagueName={selectedItem ? selectedItem.title : ""} />
                </TabPanel>
                <TabPanel value={selectedTab} index={3}>
                    <FixtureMappingView league={selectedItem} />
                </TabPanel> 
                <TabPanel value={selectedTab} index={4}>
                    <ExtFixtureView league={selectedItem} />
                </TabPanel>
            </div>

        </Container>
    );
};

export default LeagueOverview;

